import { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  ChevronDown, ChevronRight, FileText, Folder, FolderOpen,
  MoreHorizontal, Plus, FolderPlus, Copy, Star, Trash2,
} from 'lucide-react';
import type { DocumentTreeItem } from '../../services/documentService';
import { useCreateDocument, useDeleteDocument, useDuplicateDocument, useToggleDocumentFavorite } from '../../hooks/useDocuments';
import FavoriteToggle from '../ui/FavoriteToggle';

interface DocumentTreeNodeProps {
  item: DocumentTreeItem;
  depth: number;
  spaceId: string;
  // CHG-130 REQ-DHR-027: scope props 전파
  scope?: 'global' | 'project';
  projectKey?: string;
}

export default function DocumentTreeNode({ item, depth, spaceId, scope = 'global', projectKey }: DocumentTreeNodeProps) {
  const buildDocLink = (docId: string) =>
    scope === 'project' && projectKey
      ? `/projects/${projectKey}/documents/${docId}`
      : `/documents/${spaceId}/${docId}`;
  // CHG-130 REQ-DHR-027: 자동 폴더는 삭제/이동 메뉴 숨김
  const isAutoFolder = !!(item as DocumentTreeItem & { folderKey?: string | null }).folderKey;
  const hasChildren = item.children && item.children.length > 0;
  const [expanded, setExpanded] = useState(depth < 2);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const createDoc = useCreateDocument();
  const deleteDoc = useDeleteDocument();
  const duplicateDoc = useDuplicateDocument();
  const toggleFavorite = useToggleDocumentFavorite();

  useEffect(() => {
    if (!showMenu) return;
    const handle = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [showMenu]);

  const handleAddChild = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    createDoc.mutate(
      { spaceId, data: { title: '새 문서', parentDocumentId: item.id } },
      {
        onSuccess: (doc) => {
          setExpanded(true);
          navigate(buildDocLink(doc.id));
        },
      },
    );
  };

  const handleAddFolder = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    createDoc.mutate(
      { spaceId, data: { title: '새 폴더', parentDocumentId: item.id, isFolder: true } },
      { onSuccess: () => setExpanded(true) },
    );
  };

  const handleDuplicate = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    duplicateDoc.mutate(
      { spaceId, documentId: item.id },
      { onSuccess: (doc) => navigate(buildDocLink(doc.id)) },
    );
  };

  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    toggleFavorite.mutate({ spaceId, documentId: item.id });
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    const msg = item.isFolder
      ? '이 폴더를 삭제하시겠습니까? 하위 문서/폴더도 함께 삭제됩니다.'
      : '이 문서를 삭제하시겠습니까? 하위 문서도 함께 삭제됩니다.';
    if (confirm(msg)) {
      deleteDoc.mutate({ spaceId, documentId: item.id });
    }
  };

  // 폴더 클릭 시 펼침/접기만 (에디터 이동 안 함)
  const handleFolderClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setExpanded(!expanded);
  };

  // 아이콘 결정
  const renderIcon = () => {
    if (item.isFolder) {
      return expanded
        ? <FolderOpen className="size-3.5 shrink-0 text-yellow-500" />
        : <Folder className="size-3.5 shrink-0 text-yellow-500" />;
    }
    if (item.status === 'LIVE') {
      return <span className="size-2 rounded-full bg-green-500 shrink-0" />;
    }
    return <FileText className="size-3.5 shrink-0 text-gray-400" />;
  };

  const nodeContent = (
    <>
      {(hasChildren || item.isFolder) ? (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          className="p-0.5 rounded hover:bg-gray-200 shrink-0"
        >
          {expanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
        </button>
      ) : (
        <span className="w-4 shrink-0" />
      )}
      {renderIcon()}
      <span className="truncate flex-1 min-w-0">{item.title}</span>
      {!item.isFolder && item.status === 'DRAFT' && (
        <span className="text-[11px] px-1 py-0.5 bg-yellow-100 text-yellow-700 rounded shrink-0 leading-none">초안</span>
      )}
      {!item.isFolder && item.status === 'LIVE' && (
        <span className="text-[11px] px-1 py-0.5 bg-green-100 text-green-700 rounded shrink-0 leading-none">라이브</span>
      )}
      {!item.isFolder && (
        <FavoriteToggle targetType="DOCUMENT" targetId={item.id} />
      )}
    </>
  );

  const nodeClassName = (isActive?: boolean) =>
    `flex items-center gap-1.5 py-1.5 pr-7 rounded-lg text-sm transition-colors ${
      isActive ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
    }`;

  return (
    <div>
      <div className="group relative">
        {item.isFolder ? (
          // 폴더: 클릭 시 펼침/접기만 (NavLink 대신 div)
          <div
            onClick={handleFolderClick}
            className={`${nodeClassName(false)} cursor-pointer`}
            style={{ paddingLeft: `${12 + depth * 16}px` }}
          >
            {nodeContent}
          </div>
        ) : (
          // 문서: NavLink로 에디터 이동
          <NavLink
            to={buildDocLink(item.id)}
            className={({ isActive }) => nodeClassName(isActive)}
            style={{ paddingLeft: `${12 + depth * 16}px` }}
          >
            {nodeContent}
          </NavLink>
        )}

        {/* Context menu trigger */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowMenu(!showMenu);
          }}
          className="absolute right-0.5 top-1/2 -translate-y-1/2 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-gray-200 transition-opacity z-10"
        >
          <MoreHorizontal className="size-3.5 text-gray-400" />
        </button>

        {/* Context menu */}
        {showMenu && (
          <div ref={menuRef} className="absolute right-0 top-full mt-1 w-44 bg-white border rounded-lg shadow-lg z-30 py-1">
            <button
              onClick={handleAddChild}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 text-left"
            >
              <Plus className="size-3.5" />
              하위 문서 추가
            </button>
            <button
              onClick={handleAddFolder}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 text-left"
            >
              <FolderPlus className="size-3.5" />
              하위 폴더 추가
            </button>
            {!item.isFolder && (
              <button
                onClick={handleToggleFavorite}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 text-left"
              >
                <Star className="size-3.5" />
                즐겨찾기 토글
              </button>
            )}
            {!item.isFolder && (
              <button
                onClick={handleDuplicate}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 text-left"
              >
                <Copy className="size-3.5" />
                문서 복제
              </button>
            )}
            {/* CHG-130 REQ-DHR-027: 자동 폴더는 삭제 메뉴 숨김 */}
            {!isAutoFolder && (
              <button
                onClick={handleDelete}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 text-left"
              >
                <Trash2 className="size-3.5" />
                삭제
              </button>
            )}
          </div>
        )}
      </div>

      {expanded && hasChildren && (
        <div>
          {item.children.map((child) => (
            <DocumentTreeNode key={child.id} item={child} depth={depth + 1} spaceId={spaceId} scope={scope} projectKey={projectKey} />
          ))}
        </div>
      )}
    </div>
  );
}
